import { appConfig } from '@ll-platform/frontend/config/app.config';

export function isUploadcareUrl<T extends string | null | undefined>(
  url: T,
): boolean {
  return (
    url?.startsWith('https://ucarecdn.com/') ||
    url?.includes('.ucarecd.net/') ||
    url?.includes(appConfig.REACT_APP_UPLOADCARE_CDN_URL) ||
    false
  );
}

export function isUploadcareGifUrl<T extends string | null | undefined>(
  url: T,
): boolean {
  return url?.includes('/gif2video/') ?? false;
}

type OptimizeUploadcareImgArgs = {
  autoHeight?: boolean;
  autoWidth?: boolean;
};

/**
 * Downscale the image preserving the aspect ratio.
 * https://uploadcare.com/docs/transformations/image/resize-crop/#operation-preview
 */
export function optimizeUploadcareImg<T extends string | null | undefined>(
  url: T,
  maxSize?: number,
  args: OptimizeUploadcareImgArgs = {},
): T | string {
  if (typeof url !== 'string') {
    return url;
  }

  if (!isUploadcareUrl(url)) {
    console.warn(
      '[optimizeUploadcareImg] Blocked attempt to apply transformations to non-Uploadcare url ',
    );

    return url;
  }

  let newUrl = url.replace(/\/+-\/(preview|resize)\/.*?\//, '/');
  if (!newUrl.endsWith('/')) {
    newUrl += '/';
  }

  if (!maxSize) {
    return `${newUrl}-/preview/`;
  }

  if (args.autoHeight) {
    // The image will keep aspect ratio and have a max width of `maxSize`
    // 3000px is the maximum height for Uploadcare CDN
    return `${newUrl}-/preview/${maxSize}x3000/`;
  }

  if (args.autoWidth) {
    // The image will keep aspect ratio and have a max height of `maxSize`
    // 3000px is the maximum width for Uploadcare CDN
    return `${newUrl}-/preview/3000x${maxSize}/`;
  }

  return `${newUrl}-/preview/${maxSize}x${maxSize}/`;
}

export function createUploadcareThumbnailUrl<
  T extends string | null | undefined,
>(url: T, maxSize?: number): T | string {
  if (!isUploadcareGifUrl(url)) {
    return optimizeUploadcareImg(url, maxSize);
  }

  const [baseUrl] = (url as string).split('/gif2video');

  return optimizeUploadcareImg(baseUrl, maxSize);
}

export function convertUploadcareGifToVideoUrl<
  T extends string | null | undefined,
>(url: T): T | string {
  if (!isUploadcareUrl(url)) {
    console.warn(
      '[convertGifToVideoUrl] Blocked attempt to convert non-Uploadcare url ',
    );

    return url;
  }

  return `${url}gif2video/-/format/mp4/-/quality/better/`;
}
